class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__close') ||
          this.querySelector('.cart-item__name');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute(
      'aria-expanded',
      !cartDrawerNote.closest('details').hasAttribute('open')
    );

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    /*
     * Shopify has already returned the correct cart-drawer section
     * from product-form.js.
     *
     * IMPORTANT:
     * Replace the drawer section itself, not its innerHTML.
     */
    const drawerSection = parsedState.sections?.['cart-drawer'];

    if (drawerSection) {
      const parsedHTML = new DOMParser().parseFromString(
        drawerSection,
        'text/html'
      );

      const newDrawer = parsedHTML.querySelector('#CartDrawer');

      if (newDrawer) {
        const currentDrawer = this.querySelector('#CartDrawer');

        if (currentDrawer) {
          currentDrawer.replaceWith(newDrawer);
        }
      }
    }

    /*
     * Update cart icon bubble.
     */
    const cartBubbleHTML =
      parsedState.sections?.['cart-icon-bubble'];

    if (cartBubbleHTML) {
      const cartBubble = document.querySelector('#cart-icon-bubble');

      if (cartBubble) {
        const parsedBubble = new DOMParser().parseFromString(
          cartBubbleHTML,
          'text/html'
        );

        const newBubble =
          parsedBubble.querySelector('#cart-icon-bubble');

        if (newBubble) {
          cartBubble.innerHTML = newBubble.innerHTML;
        }
      }
    }

    /*
     * Reconnect the overlay after replacing #CartDrawer.
     */
    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this),
        { once: true }
      );
    }

    /*
     * Make sure the drawer knows whether it is empty.
     */
    if (typeof parsedState.item_count !== 'undefined') {
      this.classList.toggle(
        'is-empty',
        parsedState.item_count === 0
      );
    }

    /*
     * Open AFTER the new drawer HTML has been inserted.
     */
    requestAnimationFrame(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const element = parsedHTML.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);